import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import SearchIcon from '@/assets/icons/search.svg?react'
import {
  Button,
  ButtonRadio,
  Divider,
  Input,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView'
import { showCanvasError } from '@/utils/canvasHttp'

import { deleteModelRoute, setModelRouteStatus } from '../api'
import { ModelRouteListState } from '../hooks/useModelRouteList'
import { ModelRoute, routeStatusMap } from '../types'
import { ModelRouteDetail } from './ModelRouteDetail'

export function ModelRouteListView({
  status,
  setStatus,
  search,
  setSearch,
  page,
  setPage,
  routes,
  total,
  loading,
  pageSize,
}: ModelRouteListState) {
  const { t } = useTranslation('modelRoute')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [togglingResID, setTogglingResID] = useState<string | null>(null)

  const toggleRouteStatus = async (route: ModelRoute) => {
    if (togglingResID) return
    setTogglingResID(route.resID)
    try {
      await setModelRouteStatus(
        route.resID,
        route.status === 'activated' ? 'suspended' : 'activated',
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['modelRoutes'] }),
        queryClient.invalidateQueries({ queryKey: ['modelRouteKpi'] }),
      ])
    } catch (error) {
      showCanvasError(error)
    } finally {
      setTogglingResID(null)
    }
  }

  const onDeleteClick = (route: ModelRoute) => {
    Modal.confirm({
      title: (
        <Trans ns="modelRoute" i18nKey="deleteConfirm.title">
          Delete Confirmation
        </Trans>
      ),
      content: (
        <span className="t-sm text-secondary">
          <Trans
            ns="modelRoute"
            i18nKey="deleteConfirm.content"
            components={[<span key="0" className="text-error" />]}
          >
            {'You are about to <0>delete this route permanently</0>. Are you sure?'}
          </Trans>
        </span>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: (
          <Trans ns="modelRoute" i18nKey="deleteConfirm.confirmBtn">
            Confirm
          </Trans>
        ),
      },
    })
      .then(async () => {
        try {
          await deleteModelRoute(route.resID)
          message.success(t('deleteConfirm.deleted', 'Model route deleted'))
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['modelRoutes'] }),
            queryClient.invalidateQueries({ queryKey: ['modelRouteKpi'] }),
          ])
        } catch (error) {
          showCanvasError(error)
        }
      })
      .catch(() => {})
  }

  return (
    <>
      <div className="flex h-[60px] items-center justify-between">
        <div className="h6">
          <Trans ns="modelRoute" i18nKey="title">
            Model Route
          </Trans>
        </div>
        <Button type="secondary" onClick={() => navigate('/model-route/create')}>
          <Trans ns="modelRoute" i18nKey="newRouteBtn">
            New Route
          </Trans>
        </Button>
      </div>

      <Divider />

      <div className="mb-4 flex items-center justify-between gap-4">
        <ButtonRadio
          value={status}
          items={[
            { label: t('status.activated', 'Activated'), value: 'activated' },
            { label: t('status.suspended', 'Suspended'), value: 'suspended' },
          ]}
          onChange={setStatus}
        />
        <div className="relative w-60">
          <Input
            className="h-11 pl-9 t-sm"
            placeholder={t('searchPlaceholder', 'Search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none z-10" />
        </div>
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && routes.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}
      {!loading && routes.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={1}>
                <Trans ns="modelRoute" i18nKey="table.headerID">
                  ID
                </Trans>
              </th>
              <th colSpan={1}>
                <Trans ns="modelRoute" i18nKey="table.headerName">
                  Name
                </Trans>
              </th>
              <th colSpan={1}>
                <Trans ns="modelRoute" i18nKey="table.headerStatus">
                  Status
                </Trans>
              </th>
              <th colSpan={1}>
                <Trans ns="modelRoute" i18nKey="table.headerCost">
                  Cost
                </Trans>
              </th>
              <th colSpan={1}>
                <Trans ns="modelRoute" i18nKey="table.headerLastModified">
                  Last Modified
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans ns="modelRoute" i18nKey="table.headerAction">
                  Action
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {routes.map((route) => (
              <CollapseTableRow
                key={route.resID}
                colSpan={7}
                tableClassName={'!border-none ring-1 ring-neutral-200 rounded-xl group'}
                content={<ModelRouteDetail route={route} />}
              >
                <td className="t-sm" colSpan={1}>
                  {route.resID}
                </td>
                <td className="t-sm break-all" colSpan={1}>
                  {route.name}
                </td>
                <td className="t-sm" colSpan={1}>
                  <span className={routeStatusMap[route.status].style}>
                    {t(routeStatusMap[route.status].labelKey, routeStatusMap[route.status].label)}
                  </span>
                </td>
                <td className="t-sm" colSpan={1}>
                  <span className="t-sb">{route.cost}</span>{' '}
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="table.credits">
                      Credits
                    </Trans>
                  </span>
                </td>
                <td className="t-sm text-tertiary" colSpan={1}>
                  {route.lastModified}
                </td>
                <td colSpan={1}>
                  <ActionMenu
                    items={[
                      {
                        label: t('action.edit', 'Edit'),
                        onClick: () => navigate(`/model-route/${route.resID}/edit`),
                      },
                      {
                        label:
                          route.status === 'activated'
                            ? t('action.suspend', 'Suspend')
                            : t('action.activate', 'Activate'),
                        onClick: () => void toggleRouteStatus(route),
                      },
                      {
                        label: t('action.delete', 'Delete'),
                        type: 'danger',
                        onClick: () => onDeleteClick(route),
                      },
                    ]}
                    popupContainer="body"
                  />
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}

      {total > 0 && !loading && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={setPage}
        />
      )}
    </>
  )
}
